import { Badge, Checkbox, Group, Table, Text } from '@mantine/core';
import type { Dayjs } from 'dayjs';
import { tKeys, useTranslate } from 'locales/index';
import { Fragment } from 'react';
import { sumBy } from 'remeda';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';

export type ReminderResourceRow = {
  id: string;
  ariaLabel: string;
  label: string;
  date?: Dayjs;
  daysLate?: number;
  amount: number;
  checked: boolean;
  onToggle: () => void;
};

export type ReminderResourceGroup = {
  title: string;
  rows: Array<ReminderResourceRow>;
};

type ReminderResourceTableProps = {
  groups: Array<ReminderResourceGroup>;
};

const DASH = '—';

export const ReminderResourceTable = ({ groups }: ReminderResourceTableProps) => {
  const t = useTranslate();
  const formatter = useLocalizedCurrencyFormatter();

  const selectedRows = groups.flatMap(group => group.rows).filter(row => row.checked);
  const selectedTotal = sumBy(selectedRows, row => row.amount);

  return (
    <Table stickyHeader fz={12} verticalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={40} />
          <Table.Th>{t(tKeys.FORM.REFERENCE)}</Table.Th>
          <Table.Th>{t(tKeys.DUE_DATE_SHORT)}</Table.Th>
          <Table.Th>{t(tKeys.LATE)}</Table.Th>
          <Table.Th ta="right">{t(tKeys.BALANCE)}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {groups.map(group => (
          <Fragment key={group.title}>
            <Table.Tr bg="var(--mantine-color-gray-0)">
              <Table.Td colSpan={5}>
                <Text size="xs" fw="bold" c="dimmed" tt="uppercase">
                  {group.title}
                </Text>
              </Table.Td>
            </Table.Tr>
            {group.rows.map(row => (
              <Table.Tr key={row.id}>
                <Table.Td>
                  <Checkbox
                    size="xs"
                    aria-label={row.ariaLabel}
                    checked={row.checked}
                    onChange={row.onToggle}
                  />
                </Table.Td>
                <Table.Td fw="bold">{row.label}</Table.Td>
                <Table.Td c="dimmed">{row.date ? row.date.format('DD/MM/YYYY') : DASH}</Table.Td>
                <Table.Td c="dimmed">
                  {row.daysLate === undefined ? DASH : t(tKeys.DAYS, { count: row.daysLate })}
                </Table.Td>
                <Table.Td ta="right" fw="bold">
                  {formatter.format(row.amount)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Fragment>
        ))}
      </Table.Tbody>
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={5}>
            <Group
              justify="flex-end"
              gap="xs"
              role="group"
              aria-label={t(tKeys.PAYMENT_PLAN.WIZARD.STEP2.TOTAL_SELECTED)}
            >
              <Badge variant="light" size="sm">
                {selectedRows.length}
              </Badge>
              <Text size="xs" fw="bold">
                {formatter.format(selectedTotal)}
              </Text>
            </Group>
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    </Table>
  );
};
